import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CreateCustomerInput,
  Customer,
  CustomerType,
  DataEnvelope,
  ListEnvelope,
  UpdateCustomerInput,
} from './types';

export interface CustomerFilters {
  q?: string;
  type?: CustomerType | '';
  tag?: string;
}

export const customersKey = ['customers'] as const;

export function useCustomersInfinite(filters: CustomerFilters) {
  return useInfiniteQuery({
    queryKey: [...customersKey, 'list', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<ListEnvelope<Customer>>('/customers', {
        query: { ...filters, limit: 25, cursor: pageParam },
        signal,
      }),
    getNextPageParam: (last) => last.pagination.nextCursor ?? undefined,
  });
}

/** Typeahead for pickers (e.g. new ticket). */
export function useCustomerSearch(q: string) {
  return useQuery({
    queryKey: [...customersKey, 'search', q],
    enabled: q.trim().length >= 2,
    queryFn: ({ signal }) =>
      api<ListEnvelope<Customer>>('/customers', { query: { q, limit: 8 }, signal }).then((r) => r.data),
    staleTime: 10_000,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: [...customersKey, 'detail', id],
    enabled: !!id,
    queryFn: ({ signal }) => api<DataEnvelope<Customer>>(`/customers/${id}`, { signal }).then((r) => r.data),
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      api<DataEnvelope<Customer>>('/customers', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: customersKey }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomerInput }) =>
      api<DataEnvelope<Customer>>(`/customers/${id}`, { method: 'PATCH', body: input }).then((r) => r.data),
    onSuccess: (c) => {
      qc.setQueryData([...customersKey, 'detail', c.id], c);
      void qc.invalidateQueries({ queryKey: customersKey });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

export function useSyncCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<DataEnvelope<Customer>>(`/customers/${id}/sync`, { method: 'POST', body: {} }).then((r) => r.data),
    onSuccess: (c) => {
      qc.setQueryData([...customersKey, 'detail', c.id], c);
      void qc.invalidateQueries({ queryKey: customersKey });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}
